import { useState, useEffect } from "react";
import { CalendarCheck, HeartPulse, Droplet, Scale, Footprints, GlassWater, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

const METRICS = [
  { key: "bp", label: "Blood Pressure", unit: "mmHg", icon: HeartPulse, color: "#DC2626", placeholder: "120/80" },
  { key: "sugar", label: "Blood Sugar", unit: "mg/dL", icon: Droplet, color: "#0EA5E9", placeholder: "110" },
  { key: "weight", label: "Weight", unit: "kg", icon: Scale, color: "#7C3AED", placeholder: "70" },
  { key: "steps", label: "Steps", unit: "steps", icon: Footprints, color: "#16A34A", placeholder: "6000" },
  { key: "water", label: "Water", unit: "glasses", icon: GlassWater, color: "#06B6D4", placeholder: "8" },
];

export default function DailyTracker() {
  const user = useAuthStore((s) => s.user);
  const storeKey = `trata_vitals_${user?._id || "guest"}`;
  const [values, setValues] = useState({});
  const [log, setLog] = useState([]);

  useEffect(() => {
    try {
      setLog(JSON.parse(localStorage.getItem(storeKey) || "[]"));
    } catch (e) {
      setLog([]);
    }
  }, [storeKey]);

  const save = () => {
    const filled = Object.entries(values).filter(([, v]) => v && String(v).trim());
    if (filled.length === 0) return toast.error("Enter at least one reading");
    const entry = { date: new Date().toISOString(), ...Object.fromEntries(filled) };
    const next = [entry, ...log].slice(0, 30);
    setLog(next);
    localStorage.setItem(storeKey, JSON.stringify(next));
    setValues({});
    toast.success("Reading saved");
  };

  return (
    <div className="max-w-3xl" data-testid="daily-tracker-page">
      <PageHeader title="Daily Tracker" subtitle="Log your daily vitals" icon={CalendarCheck} color="#16A34A" />

      <Card className="rounded-3xl p-5 border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {METRICS.map((m) => {
            const MIcon = m.icon;
            return (
              <div key={m.key}>
                <label className="flex items-center gap-2 text-sm font-medium mb-1.5">
                  <MIcon className="h-4 w-4" style={{ color: m.color }} /> {m.label} <span className="text-xs text-muted-foreground">({m.unit})</span>
                </label>
                <Input
                  data-testid={`vital-${m.key}`}
                  value={values[m.key] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [m.key]: e.target.value }))}
                  placeholder={m.placeholder}
                  className="rounded-2xl h-11"
                />
              </div>
            );
          })}
        </div>
        <Button onClick={save} className="w-full mt-5 h-12 rounded-2xl bg-[#16A34A]" data-testid="vital-save">
          <Plus className="h-4 w-4 mr-1.5" /> Save Today's Reading
        </Button>
      </Card>

      <h2 className="text-lg font-semibold mt-6 mb-3">Recent Readings</h2>
      {log.length === 0 ? (
        <p className="text-sm text-muted-foreground">No readings yet. Log your first above.</p>
      ) : (
        <div className="space-y-2">
          {log.map((e, i) => (
            <Card key={i} className="rounded-2xl p-3.5 border-border" data-testid={`vital-log-${i}`}>
              <p className="text-xs text-muted-foreground">{new Date(e.date).toLocaleString()}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm">
                {METRICS.filter((m) => e[m.key]).map((m) => (
                  <span key={m.key} className="font-medium">{m.label}: <span style={{ color: m.color }}>{e[m.key]}</span> {m.unit}</span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
